import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Check, Loader2, CloudOff } from 'lucide-react';

import { useIntake } from '../context/IntakeContext';
import { STEPS, stepIndexFromParam } from '../steps';
import NotFoundPage from '../pages/NotFoundPage';
import PoweredBy from './PoweredBy';
import { applyBranding } from '../lib/branding';
import { cn } from '../lib/utils';

/**
 * Frame around every step: the shop's branding, where the customer is up to,
 * and the save indicator.
 *
 * The save indicator matters more here than it would in an internal tool — the
 * whole promise of the new flow is "you can stop at any point", so the customer
 * has to be able to see that it is true.
 */

const SaveIndicator = () => {
  const { saving, saveError, started } = useIntake();

  if (!started) return null;

  if (saveError) {
    // The message is on the title so a customer on the phone to the shop can
    // read out what actually went wrong; the console carries the full detail.
    return (
      <span className="flex items-center gap-1.5 text-xs text-destructive" title={saveError}>
        <CloudOff className="h-3.5 w-3.5" />
        Not saved — retrying
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {saving ? (
        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
      ) : (
        <><Check className="h-3.5 w-3.5 text-green-600" /> Saved</>
      )}
    </span>
  );
};

const StepProgress = ({ currentIndex }) => (
  <div className="flex items-center gap-1.5" aria-hidden="true">
    {STEPS.map((step, index) => (
      <span
        key={step.key}
        className={cn(
          'h-1 flex-1 rounded-full transition-colors',
          index <= currentIndex ? 'bg-[var(--brand-accent)]' : 'bg-muted'
        )}
      />
    ))}
  </div>
);

const IntakeShell = ({ children }) => {
  const { organisation, loading, error } = useIntake();
  const location = useLocation();

  // Apply the shop's palette, if they have set one.
  useEffect(() => {
    if (!organisation?.branding) return undefined;
    return applyBranding(document.documentElement, organisation.branding);
  }, [organisation]);

  useEffect(() => {
    if (organisation?.name) {
      document.title = `Quote request — ${organisation.name}`;
    }
  }, [organisation]);

  // Each step starts at the top; on a phone the previous scroll position is
  // almost always the wrong place to land.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) return <NotFoundPage error={error} />;

  // Steps are addressed by number, so the last path segment is the position.
  const rest = (location.pathname.split('/form')[1] || '').replace(/^\//, '');
  const isDone = rest === 'done';
  const currentIndex = Math.max(0, stepIndexFromParam(rest));

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <header className="sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-black/5">
        <div className="mx-auto w-full max-w-2xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              {organisation?.branding?.logoUrl ? (
                <img
                  src={organisation.branding.logoUrl}
                  alt={organisation.name}
                  className="h-8 w-auto max-w-[140px] object-contain"
                />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)]">
                  <Building2 className="h-4 w-4" />
                </span>
              )}
              <span className="truncate font-semibold">{organisation?.name}</span>
            </div>
            <SaveIndicator />
          </div>

          {!isDone && (
            <div className="mt-3">
              <StepProgress currentIndex={currentIndex} />
              <p className="mt-1.5 text-xs text-muted-foreground">
                Step {currentIndex + 1} of {STEPS.length} — {STEPS[currentIndex]?.label}
              </p>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl px-4 py-6">{children}</main>

      <footer className="mx-auto w-full max-w-2xl px-4 pb-safe pt-2">
        <PoweredBy />
      </footer>
    </div>
  );
};

export default IntakeShell;
